import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  useSyncExternalStore,
  type ReactNode,
} from 'react';
import { useLocation } from 'react-router-dom';
import { needsTermsAcceptance } from '@floorpoll/shared';
import { useAuth } from '../auth.js';
import { useWorkspace } from '../workspace.js';
import { AnchorContext, createAnchorStore, type AnchorStore } from './anchors.js';
import { routeMatches, stepsFor, type TourStep } from './steps.js';

const GUIDE_KEY = 'floorpoll.guide';

/**
 * Whether this browser has already been offered the guide — the whole of what is
 * persisted.
 *
 * Step position stays in memory on purpose. Resuming mid-run across sessions
 * would mean writing progress on every step, and the privacy policy enumerates
 * browser storage exhaustively: a bigger record is a bigger disclosure for a
 * feature nobody asked for.
 */
type GuideOutcome = 'done' | 'skipped';

function readOutcome(): GuideOutcome | null {
  try {
    const raw = localStorage.getItem(GUIDE_KEY);
    if (!raw) return null;
    const parsed: unknown = JSON.parse(raw);
    if (typeof parsed !== 'object' || parsed === null) return null;
    const status = (parsed as { status?: unknown }).status;
    return status === 'done' || status === 'skipped' ? status : null;
  } catch {
    // Malformed or unavailable storage means "not seen yet". A private-mode
    // browser that throws on read should still get the guide, not a crash.
    return null;
  }
}

function writeOutcome(status: GuideOutcome): void {
  try {
    localStorage.setItem(GUIDE_KEY, JSON.stringify({ v: 1, status }));
  } catch {
    // Storage denied. The guide ran; it will simply offer itself again.
  }
}

/**
 * `off` — not running. `running` — a step is on screen. `standing-by` — the run
 * is alive but the current step belongs to a page the user is not on, so the
 * guide docks instead of pointing at nothing.
 */
type Phase = 'off' | 'running' | 'standing-by';

interface TourValue {
  phase: Phase;
  step: TourStep | null;
  /** 1-based, for display. */
  position: number;
  total: number;
  /** Where the guide is waiting to resume, when standing by. */
  standingByOn: 'polls' | 'builder' | null;
  start: () => void;
  next: () => void;
  back: () => void;
  skip: () => void;
}

const TourContext = createContext<TourValue | null>(null);

export function TourProvider({ children }: { children: ReactNode }) {
  const { pathname } = useLocation();
  const { status, user } = useAuth();
  const { active, can } = useWorkspace();

  const store = useMemo<AnchorStore>(() => createAnchorStore(), []);
  const steps = useMemo(() => stepsFor(can), [can]);

  const [phase, setPhase] = useState<Phase>('off');
  const [index, setIndex] = useState(0);
  // Read once. Re-reading would let a finish in another tab yank a run in progress.
  const [seen, setSeen] = useState<GuideOutcome | null>(readOutcome);

  const step = phase === 'off' ? null : (steps[index] ?? null);

  const finish = useCallback((outcome: GuideOutcome) => {
    setPhase('off');
    setIndex(0);
    setSeen(outcome);
    writeOutcome(outcome);
  }, []);

  const start = useCallback(() => {
    setIndex(0);
    setPhase('running');
  }, []);

  // The terminal check sits outside the updater: a state updater has to be pure,
  // and StrictMode runs it twice.
  const next = useCallback(() => {
    if (index + 1 >= steps.length) {
      finish('done');
      return;
    }
    setIndex(index + 1);
  }, [index, steps.length, finish]);

  const back = useCallback(() => {
    setIndex((current) => Math.max(0, current - 1));
  }, []);

  const skip = useCallback(() => finish('skipped'), [finish]);

  /**
   * Offer the guide once, and only on the dashboard.
   *
   * Restricting it to `/polls` is what keeps it away from a presenter: the
   * builder and the controls are reachable directly by URL mid-event, and an
   * overlay arriving over live controls is the same mistake the README warns
   * about for terms bumps. `/` redirects to `/polls`, so a new account still
   * meets it on arrival.
   */
  useEffect(() => {
    if (seen || phase !== 'off') return;
    if (status !== 'signed-in' || needsTermsAcceptance(user)) return;
    // Without an active workspace `can` answers false to everything and the run
    // would be filtered down to the viewer-safe steps.
    if (!active) return;
    if (pathname !== '/polls') return;
    start();
  }, [seen, phase, status, user, active, pathname, start]);

  /**
   * Keep the run and the address bar in agreement. A step that belongs to another
   * page docks the guide; arriving on that page brings it back up. This is also
   * what carries the hand-off — creating a poll lands on the builder, where the
   * builder chapter is already the current step.
   */
  useEffect(() => {
    if (phase === 'off' || !step) return;
    const here = routeMatches(step.route, pathname);
    if (here && phase === 'standing-by') setPhase('running');
    if (!here && phase === 'running') setPhase('standing-by');
  }, [phase, step, pathname]);

  /**
   * Doing the thing is the Next button: when a step names an anchor that does not
   * exist yet, the guide advances the moment that element mounts.
   *
   * Armed only if the anchor is **absent** when the step becomes current, which
   * is what keeps Back working. A plain "advance whenever it is present" rule
   * traps the run: stepping back onto the add-screen step, with the screen the
   * user just made still on the page, would immediately throw them forward
   * again. It also means replaying the guide on a poll that already has screens
   * still shows the step rather than blinking past it.
   */
  const anchorVersion = useSyncExternalStore(store.subscribe, store.getVersion, () => 0);
  const armedFor = useRef<string | null>(null);

  useEffect(() => {
    const awaiting = phase === 'running' ? step?.advanceOnAnchor : undefined;
    if (!awaiting || !step) {
      armedFor.current = null;
      return;
    }

    const present = Boolean(store.get(awaiting));

    // Arrival at this step: decide once whether there is anything to wait for.
    if (armedFor.current !== step.id) {
      armedFor.current = present ? null : step.id;
      return;
    }

    if (present) {
      armedFor.current = null;
      next();
    }
  }, [phase, step, store, anchorVersion, next]);

  const value = useMemo<TourValue>(
    () => ({
      phase,
      step,
      position: index + 1,
      total: steps.length,
      standingByOn: phase === 'standing-by' ? (step?.route ?? null) : null,
      start,
      next,
      back,
      skip,
    }),
    [phase, step, index, steps.length, start, next, back, skip],
  );

  return (
    <AnchorContext.Provider value={store}>
      <TourContext.Provider value={value}>{children}</TourContext.Provider>
    </AnchorContext.Provider>
  );
}

export function useTour(): TourValue {
  const value = useContext(TourContext);
  if (!value) throw new Error('useTour must be used inside a TourProvider');
  return value;
}
